import React, { useState, useEffect } from 'react';
import { Wallet, FileText, Droplet, Phone, Download, Smartphone } from 'lucide-react';
import useIdleLogout from '../../hooks/useIdleLogout';
import useBackButtonLogout from '../../hooks/useBackButtonLogout';
import AgreementGate from './AgreementGate';
import { API_BASE } from '../../config';
import AppShell from '../../layout/AppShell';
import { tenantNav } from '../../layout/navConfig';
import SectionCard from '../../components/ui/SectionCard';
import DataTable from '../../components/ui/DataTable';
import StatusBadge from '../../components/ui/StatusBadge';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';

const KENYAN_PHONE_RE = /^(07|01)\d{8}$/;

function TenantDashboard() {

  useIdleLogout(5);
  useBackButtonLogout();

  const [tenant, setTenant]     = useState(null);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [waterBills, setWaterBills] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  const [altPhone, setAltPhone]                 = useState(null);
  const [altPhoneInput, setAltPhoneInput]        = useState('');
  const [editingAltPhone, setEditingAltPhone]    = useState(false);
  const [altPhoneError, setAltPhoneError]        = useState('');
  const [savingAltPhone, setSavingAltPhone]      = useState(false);

  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payPhone, setPayPhone] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [payStatus, setPayStatus] = useState('');
  const [payError, setPayError] = useState('');

  async function loadData() {
    try {
      const summaryRes = await fetch(`${API_BASE}/api/tenants/me/summary`, {
        credentials: 'include',
      });
      const summaryData = await summaryRes.json();
      if (!summaryRes.ok) {
        if (summaryRes.status === 401) {
          window.location.href = '/login';
          return;
        }
        setError(summaryData.error || 'Could not load your details.');
        return;
      }
      setTenant(summaryData);

      const paymentsRes = await fetch(`${API_BASE}/api/payments`, {
        credentials: 'include',
      });
      const paymentsData = await paymentsRes.json();
      if (paymentsRes.ok) setPayments(paymentsData);

      const invoicesRes = await fetch(`${API_BASE}/api/invoices`, {
        credentials: 'include',
      });
      const invoicesData = await invoicesRes.json();
      if (invoicesRes.ok) setInvoices(invoicesData);

      const phonesRes = await fetch(`${API_BASE}/api/tenants/me/payment-phones`, {
        credentials: 'include',
      });
      const phonesData = await phonesRes.json();
      if (phonesRes.ok) setAltPhone(phonesData.alt_phone);

      if (summaryData.has_water_bill && invoicesData?.length > 0) {
        const unitId = invoicesData[0].unit_id;
        const waterRes = await fetch(`${API_BASE}/api/water-bills/${unitId}`, {
          credentials: 'include',
        });
        const waterData = await waterRes.json();
        if (waterRes.ok) setWaterBills(waterData);
      }
    } catch (err) {
      setError('Could not reach the server.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleLogout() {
    fetch(`${API_BASE}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }).finally(() => {
      window.location.href = '/login';
    });
  }

  function handlePay() {
    window.location.href = `/pay?unit=${tenant.unit_number}`;
  }

  async function handleDownloadReceipt(paymentId) {
    try {
      const res = await fetch(`${API_BASE}/api/payments/${paymentId}/receipt/pdf`, {
        credentials: 'include',
      });
      if (!res.ok) {
        alert('Could not generate receipt PDF.');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt-RCT-${String(paymentId).padStart(5, '0')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Could not reach the server.');
    }
  }

  async function handleDownloadInvoiceReceipt(invoiceId) {
    try {
      const res = await fetch(`${API_BASE}/api/invoices/${invoiceId}/receipt/pdf`, {
        credentials: 'include',
      });
      if (!res.ok) {
        alert('Could not generate receipt PDF.');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rent-receipt-INV-${String(invoiceId).padStart(5, '0')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Could not reach the server.');
    }
  }

  async function handleDownloadAgreement() {
    try {
      const res = await fetch(`${API_BASE}/api/tenants/me/agreement/pdf`, {
        credentials: 'include',
      });
      if (!res.ok) {
        alert('Could not generate the agreement PDF.');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tenancy-agreement-house-${tenant.unit_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Could not reach the server.');
    }
  }

  async function handleDownloadDepositReceipt() {
    try {
      const res = await fetch(`${API_BASE}/api/tenants/${tenant.user_id}/deposit-receipt/pdf`, {
        credentials: 'include',
      });
      if (!res.ok) {
        alert('Could not generate deposit receipt PDF.');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `deposit-receipt-DEP-${String(tenant.user_id).padStart(5, '0')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Could not reach the server.');
    }
  }

  async function handleSaveAltPhone() {
    if (!altPhoneInput) {
      setAltPhoneError('Enter a phone number.');
      return;
    }
    setAltPhoneError('');
    setSavingAltPhone(true);
    try {
      const res = await fetch(`${API_BASE}/api/tenants/me/payment-phones`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone: altPhoneInput }),
      });
      const data = await res.json();
      if (!res.ok) {
        setAltPhoneError(data.error || 'Could not save this number.');
        return;
      }
      setAltPhone(data.alt_phone);
      setAltPhoneInput('');
      setEditingAltPhone(false);
    } catch (err) {
      setAltPhoneError('Could not reach the server.');
    } finally {
      setSavingAltPhone(false);
    }
  }

  async function handleRemoveAltPhone() {
    setSavingAltPhone(true);
    try {
      const res = await fetch(`${API_BASE}/api/tenants/me/payment-phones`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        setAltPhone(null);
        setAltPhoneInput('');
        setAltPhoneError('');
      }
    } catch (err) {
      alert('Could not reach the server.');
    } finally {
      setSavingAltPhone(false);
    }
  }

  function getTotal() {
    if (!tenant) return 0;
    return tenant.total_due;
  }

  function getPaymentStatus() {
    if (tenant.already_paid_this_month) return { label: 'Paid up', tone: 'good' };
    if (tenant.penalty > 0) return { label: 'Overdue', tone: 'danger' };
    return { label: 'Due soon', tone: 'warning' };
  }

  function getDueDate() {
    const current = invoices.find((inv) => inv.month === tenant.current_month && inv.status !== 'paid');
    return current?.due_date || null;
  }

  function openPayModal() {
    setPayPhone(tenant.phone || '');
    setPayAmount(getTotal() > 0 ? String(getTotal()) : '');
    setPayError('');
    setPayStatus('');
    setPayModalOpen(true);
  }

  async function pollPayStatus(checkoutRequestId) {
    for (let attempt = 0; attempt < 30; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      try {
        const res = await fetch(`${API_BASE}/api/mpesa/status/${checkoutRequestId}`);
        const data = await res.json();
        if (data.status === 'success') {
          setPayStatus('Payment confirmed! Updating your dashboard...');
          await loadData();
          setPaySubmitting(false);
          setTimeout(() => setPayModalOpen(false), 1200);
          return;
        }
        if (data.status === 'failed') {
          setPayError(data.result_desc || 'Payment was not completed. Please try again.');
          setPayStatus('');
          setPaySubmitting(false);
          return;
        }
        setPayStatus('Waiting for you to complete the payment on your phone...');
      } catch (err) {
        // transient network hiccup — keep polling
      }
    }
    setPayError('This is taking longer than expected. Check your invoices shortly, or try again.');
    setPayStatus('');
    setPaySubmitting(false);
  }

  async function handleSubmitPay() {
    const cleanPhone = payPhone.replace(/\s/g, '');
    if (!KENYAN_PHONE_RE.test(cleanPhone)) {
      setPayError('Enter a valid Kenyan number, e.g. 0712 345 678');
      return;
    }
    const parsedAmount = parseFloat(payAmount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setPayError('Enter an amount greater than zero.');
      return;
    }

    const unitId = invoices[0]?.unit_id;
    if (!unitId) {
      // No invoice on file yet to source the unit id from — fall back to
      // the full pay page, same destination the old "Pay via M-Pesa" used.
      handlePay();
      return;
    }

    setPayError('');
    setPaySubmitting(true);
    setPayStatus('Sending the request to your phone...');

    try {
      const res = await fetch(`${API_BASE}/api/mpesa/stkpush`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ unit_id: unitId, phone: cleanPhone, amount: parsedAmount }),
      });
      const data = await res.json();
      if (!res.ok) {
        setPayError(data.error || 'Payment failed. Please try again.');
        setPayStatus('');
        setPaySubmitting(false);
        return;
      }
      setPayStatus('Check your phone and enter your M-Pesa PIN to complete the payment.');
      pollPayStatus(data.checkout_request_id);
    } catch (err) {
      setPayError('Could not reach the server. Please try again.');
      setPayStatus('');
      setPaySubmitting(false);
    }
  }

  if (loading) {
    return (
      <div style={styles.centered}>
        <p style={styles.loadingText}>Loading your dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.centered}>
        <div style={{ textAlign: 'center' }}>
          <p style={styles.errorText}>⚠ {error}</p>
          <a href="/login" style={styles.backToLoginLink} className="link-underline">
            ← Back to Login
          </a>
        </div>
      </div>
    );
  }

  if (!tenant.agreement_signed) {
    return <AgreementGate />;
  }

  if (tenant.id_photo_requested) {
    return <AgreementGate photoOnly />;
  }

  if (!tenant.deposit_paid) {
    return (
      <div style={styles.centered}>
        <div style={styles.pendingCard}>
          <p style={styles.pendingIcon}>⏳</p>
          <h2 style={styles.pendingTitle}>Almost there</h2>
          <p style={styles.pendingText}>
            {tenant.deposit_amount_paid > 0
              ? `We've recorded Ksh ${tenant.deposit_amount_paid.toLocaleString()} of your Ksh ${(tenant.unit_deposit_amount ?? 0).toLocaleString()} deposit so far. Please pay the remaining Ksh ${Math.max((tenant.unit_deposit_amount ?? 0) - tenant.deposit_amount_paid, 0).toLocaleString()} to get full access to your dashboard.`
              : "Your deposit payment is pending admin confirmation. Once the office has recorded your deposit, you'll get full access to your dashboard."}
          </p>
          <button onClick={handleLogout} style={styles.pendingLogoutBtn} className="btn-lift">
            Logout
          </button>
        </div>
      </div>
    );
  }

  const paymentStatus = getPaymentStatus();
  const dueDate = getDueDate();

  return (
    <AppShell
      navGroups={tenantNav}
      activeKey="dashboard"
      onNavigate={() => {}}
      userName={tenant.full_name}
      userRole="Tenant"
      onLogout={handleLogout}
      pageTitle="Dashboard"
      breadcrumb={`House ${tenant.unit_number} · ${tenant.property_name}`}
    >
      <div style={styles.stack}>

        {/* Hero */}
        <div style={styles.hero}>
          <div style={styles.heroLeft}>
            <p style={styles.heroLabel}>
              {tenant.already_paid_this_month ? "This month's balance" : 'Balance Due'}
            </p>
            <p style={styles.heroAmount}>Ksh {getTotal().toLocaleString()}</p>
            <div style={styles.heroMetaRow}>
              <span style={styles.heroMetaItem}>House {tenant.unit_number}</span>
              {dueDate && <span style={styles.heroMetaItem}>Due {dueDate}</span>}
              <StatusBadge status={paymentStatus.label} />
            </div>
            {tenant.invoice_status === 'partial' && (
              <p style={styles.heroNote}>
                You've paid Ksh {tenant.amount_paid_on_invoice.toLocaleString()} towards {tenant.current_month} so far.
              </p>
            )}
            {tenant.penalty > 0 && (
              <p style={styles.heroNote}>
                ⚠ Includes a Ksh {tenant.penalty.toLocaleString()} late penalty, added since rent wasn't paid by the 5th.
              </p>
            )}
          </div>
          <Button
            variant="primary"
            icon={Wallet}
            onClick={openPayModal}
            style={styles.heroPayBtn}
          >
            Pay with M-Pesa
          </Button>
        </div>

        {payModalOpen && (
          <Modal title="Pay Rent" onClose={() => !paySubmitting && setPayModalOpen(false)} width="420px">
            <div style={styles.payForm}>
              <div style={styles.field}>
                <label style={styles.label} htmlFor="pay-phone">M-Pesa Phone Number</label>
                <input
                  id="pay-phone"
                  className="input-field"
                  style={styles.input}
                  value={payPhone}
                  onChange={(e) => setPayPhone(e.target.value)}
                  placeholder="0712 345 678"
                  type="tel"
                  disabled={paySubmitting}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label} htmlFor="pay-amount">Amount (Ksh)</label>
                <input
                  id="pay-amount"
                  className="input-field"
                  style={styles.input}
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  placeholder="e.g. 5000"
                  type="number"
                  min="1"
                  disabled={paySubmitting}
                />
              </div>
              <Button
                variant="primary"
                icon={Smartphone}
                onClick={handleSubmitPay}
                disabled={paySubmitting}
                style={{ width: '100%', justifyContent: 'center' }}
              >
                {paySubmitting ? 'Sending...' : 'Send M-Pesa Prompt'}
              </Button>
              {payStatus !== '' && <p style={styles.payStatusText}>📱 {payStatus}</p>}
              {payError !== '' && <p style={styles.payErrorText}>{payError}</p>}
            </div>
          </Modal>
        )}

        <div style={styles.cardsGrid}>
          <SectionCard title="Recent Payments">
            <DataTable
              columns={[
                { key: 'month', label: 'Month' },
                { key: 'amount', label: 'Amount', render: (p) => `KSh ${p.amount.toLocaleString()}` },
                { key: 'status', label: 'Status', render: (p) => <StatusBadge status={p.status} /> },
              ]}
              rows={payments}
              rowKey={(p) => p.payment_id}
              emptyIcon={Wallet}
              emptyTitle="No payments recorded yet"
              actions={(p) => (p.status === 'paid' ? [
                { label: 'Download Receipt', onClick: () => handleDownloadReceipt(p.payment_id) },
              ] : [])}
            />
          </SectionCard>

          <SectionCard title="Invoices">
            <DataTable
              columns={[
                { key: 'invoice_no', label: 'Invoice' },
                { key: 'month', label: 'Month' },
                {
                  key: 'amount',
                  label: 'Amount',
                  render: (inv) => inv.status === 'partial'
                    ? `KSh ${inv.balance_due.toLocaleString()} due`
                    : `KSh ${inv.total_amount.toLocaleString()}`,
                },
                { key: 'status', label: 'Status', render: (inv) => <StatusBadge status={inv.status} /> },
              ]}
              rows={invoices}
              rowKey={(inv) => inv.invoice_id}
              emptyIcon={FileText}
              emptyTitle="No invoices yet"
              actions={(inv) => (inv.amount_paid > 0 ? [
                { label: 'Download Receipt', onClick: () => handleDownloadInvoiceReceipt(inv.invoice_id) },
              ] : [])}
            />
          </SectionCard>

          {Boolean(tenant.has_water_bill) && (
            <SectionCard title="Water Bills">
              <DataTable
                columns={[
                  { key: 'month', label: 'Month' },
                  { key: 'year', label: 'Year' },
                  { key: 'amount', label: 'Amount', render: (b) => `KSh ${b.amount.toLocaleString()}` },
                ]}
                rows={waterBills}
                rowKey={(b) => b.bill_id}
                emptyIcon={Droplet}
                emptyTitle="No water bills recorded yet"
              />
            </SectionCard>
          )}

          <SectionCard title="Property Contacts">
            <div style={styles.contactList}>
              <div style={styles.contactRow}>
                <span style={styles.contactLabel}>Property</span>
                <span style={styles.contactValue}>{tenant.property_name}</span>
              </div>
              {tenant.agent_name && (
                <div style={styles.contactRow}>
                  <span style={styles.contactLabel}>Agent</span>
                  <span style={styles.contactValue}>
                    {tenant.agent_name}{tenant.agent_phone ? ` · ${tenant.agent_phone}` : ''}
                  </span>
                </div>
              )}
              {tenant.caretaker_name && (
                <div style={{ ...styles.contactRow, borderBottom: 'none' }}>
                  <span style={styles.contactLabel}>Caretaker</span>
                  <span style={styles.contactValue}>
                    {tenant.caretaker_name}{tenant.caretaker_phone ? ` · ${tenant.caretaker_phone}` : ''}
                  </span>
                </div>
              )}
              {!tenant.agent_name && !tenant.caretaker_name && (
                <p style={{ fontSize: '13px', color: 'var(--color-muted)', margin: 0 }}>
                  No agent or caretaker assigned to your property yet.
                </p>
              )}
            </div>
          </SectionCard>

          <SectionCard title="My Documents">
            <div style={styles.docButtons}>
              <Button variant="secondary" icon={Download} onClick={handleDownloadAgreement}>
                Tenancy Agreement
              </Button>
              <Button variant="secondary" icon={Download} onClick={handleDownloadDepositReceipt}>
                Deposit Receipt
              </Button>
            </div>
          </SectionCard>

          <SectionCard title="Payment Numbers">
            <p style={styles.helperText}>
              If someone else pays your rent on your behalf (e.g. from their own phone),
              register their number here so the payment can still be linked to you.
            </p>

            <div style={styles.contactRow}>
              <span style={styles.contactLabel}>Your Registered Number</span>
              <span style={styles.contactValue}>{tenant.phone}</span>
            </div>

            <div style={{ ...styles.contactRow, borderBottom: 'none', alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={styles.contactLabel}>Alternate Number</span>
                {!editingAltPhone && altPhone && <p style={styles.phoneValue}>{altPhone}</p>}
                {!editingAltPhone && !altPhone && (
                  <p style={{ ...styles.phoneValue, color: 'var(--color-muted)' }}>None registered</p>
                )}
                {editingAltPhone && (
                  <input
                    type="text"
                    value={altPhoneInput}
                    onChange={(e) => setAltPhoneInput(e.target.value)}
                    placeholder="0712345678"
                    className="input-field"
                    style={styles.input}
                  />
                )}
              </div>

              {editingAltPhone ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button size="sm" variant="primary" onClick={handleSaveAltPhone} disabled={savingAltPhone}>
                    {savingAltPhone ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => { setEditingAltPhone(false); setAltPhoneInput(''); setAltPhoneError(''); }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={Phone}
                    onClick={() => { setEditingAltPhone(true); setAltPhoneInput(altPhone || ''); }}
                  >
                    {altPhone ? 'Change' : 'Add'}
                  </Button>
                  {altPhone && (
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={handleRemoveAltPhone}
                      disabled={savingAltPhone}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              )}
            </div>
            {altPhoneError !== '' && (
              <p style={{ color: 'var(--color-danger-strong)', fontSize: '13px', margin: '12px 0 0' }}>{altPhoneError}</p>
            )}
          </SectionCard>
        </div>
      </div>
    </AppShell>
  );
}

const styles = {
  centered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    backgroundColor: 'var(--color-bg)',
  },
  pendingCard: {
    background: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    padding: '40px 32px',
    maxWidth: '400px',
    width: '100%',
    textAlign: 'center',
    boxShadow: 'var(--shadow-lg)',
    border: '1px solid var(--color-border)',
    margin: '0 16px',
    boxSizing: 'border-box',
  },
  pendingIcon: { fontSize: '40px', margin: '0 0 16px' },
  pendingTitle: { fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: 800, color: 'var(--color-ink)', margin: '0 0 12px' },
  pendingText: { fontSize: '14px', color: 'var(--color-ink-soft)', lineHeight: 1.6, margin: '0 0 28px' },
  pendingLogoutBtn: {
    padding: '12px 28px',
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-primary)',
    border: '2px solid var(--color-primary)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '14px',
    fontWeight: 700,
  },
  loadingText: { color: 'var(--color-muted)', fontSize: '15px' },
  errorText: { color: 'var(--color-danger)', fontSize: '15px', textAlign: 'center' },
  backToLoginLink: {
    display: 'inline-block',
    marginTop: '16px',
    fontSize: '14px',
    fontWeight: 700,
    color: 'var(--color-primary)',
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  hero: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '20px',
    background: 'linear-gradient(135deg, var(--color-primary), var(--color-primary-dark))',
    color: 'var(--color-text-on-brand)',
    borderRadius: 'var(--radius-lg)',
    padding: '28px 32px',
  },
  heroLeft: {
    minWidth: '240px',
  },
  heroLabel: {
    fontSize: '13px',
    fontWeight: 600,
    opacity: 0.85,
    margin: '0 0 6px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  heroAmount: {
    fontFamily: 'var(--font-display)',
    fontSize: '36px',
    fontWeight: 800,
    margin: '0 0 12px',
    fontVariantNumeric: 'tabular-nums',
  },
  heroMetaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '10px',
  },
  heroMetaItem: {
    fontSize: '13px',
    fontWeight: 600,
    opacity: 0.9,
    padding: '3px 10px',
    borderRadius: '999px',
    backgroundColor: 'rgba(255,255,255,0.16)',
  },
  heroNote: {
    fontSize: '13px',
    opacity: 0.9,
    margin: '12px 0 0',
    maxWidth: '420px',
  },
  heroPayBtn: {
    padding: '14px 26px',
    fontSize: '15px',
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-primary)',
  },
  payForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  field: {},
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--color-ink-soft)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    border: '1.5px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '14px',
    boxSizing: 'border-box',
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-ink)',
  },
  payStatusText: {
    fontSize: '13px',
    color: 'var(--color-primary-dark)',
    backgroundColor: 'var(--color-primary-light)',
    padding: '10px 14px',
    borderRadius: 'var(--radius-sm)',
    margin: 0,
  },
  payErrorText: {
    fontSize: '13px',
    color: 'var(--color-danger)',
    backgroundColor: 'var(--color-danger-light)',
    padding: '10px 14px',
    borderRadius: 'var(--radius-sm)',
    margin: 0,
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
    gap: '20px',
    alignItems: 'start',
  },
  contactList: {
    display: 'flex',
    flexDirection: 'column',
  },
  contactRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '10px 0',
    borderBottom: '1px solid var(--color-border)',
    gap: '16px',
  },
  contactLabel: { fontSize: '12px', color: 'var(--color-ink-soft)', fontWeight: 600, display: 'block', marginBottom: '2px' },
  contactValue: { fontSize: '14px', fontWeight: 600, color: 'var(--color-ink)', textAlign: 'right' },
  phoneValue: { fontSize: '14px', fontWeight: 600, margin: 0, color: 'var(--color-ink)' },
  docButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  helperText: {
    fontSize: '13px',
    color: 'var(--color-muted)',
    margin: '-6px 0 14px',
  },
};

export default TenantDashboard;
